import MonthRangeSelector, { MonthRange } from '@/components/MonthRangeSelector'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import dayjs from 'dayjs'
import { Download, LayoutGrid, Table } from 'lucide-react'
import { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Button } from 'vnetwork-ui'
import {
    exportCustomerRevenueCombined,
    getCustomerRevenueCombined,
} from '../../../services'
import { getColumns } from './columns'
import OverviewTable from './OverviewTable'
import StatisticsCards from './StatisticsCards'

const today = dayjs()

interface RevenueFilters {
    gte: string // YYYY-MM
    lte: string // YYYY-MM
    view: string // 'overview' | 'table'
}

const RevenueAllocation: React.FC = () => {
    const { t } = useTranslation('customer')
    const { id } = useParams()
    const customerId = Number(id)
    const [isExporting, setIsExporting] = useState(false)

    const { params, filters, setFilter, setFilters, setParams } =
        useUrlSearchParams<RevenueFilters>({
            defaults: {
                page_size: 50,
                sort_by: 'allocated_amount',
                sort_order: 'desc',
            },
            filterDefaults: {
                gte: today.startOf('quarter').format('YYYY-MM'),
                lte: today.endOf('quarter').format('YYYY-MM'),
                view: 'overview',
            },
            prefix: 'rev',
        })

    const viewMode = filters.view || 'overview'

    // Derive MonthRange for selector (YYYY-MM-DD format)
    const monthRange: MonthRange = useMemo(
        () => ({
            gte: dayjs(filters.gte).startOf('month').format('YYYY-MM-DD'),
            lte: dayjs(filters.lte).endOf('month').format('YYYY-MM-DD'),
        }),
        [filters.gte, filters.lte]
    )

    // API params (flat, not base64)
    const apiParams = useMemo(
        () => ({
            gte: filters.gte,
            lte: filters.lte,
            page: params.page,
            page_size: params.page_size,
            sort_by: params.sort_by,
            sort_order: params.sort_order,
        }),
        [
            filters.gte,
            filters.lte,
            params.page,
            params.page_size,
            params.sort_by,
            params.sort_order,
        ]
    )

    // Fetch data
    const { data: response, isLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CUSTOMER_REVENUE_COMBINED, customerId, apiParams],
        queryFn: () => getCustomerRevenueCombined(customerId, apiParams),
        enabled: !!customerId,
        staleTime: 5 * 60 * 1000,
    })

    const data = response?.results || []
    const pagination = response?.pagination
    const summary = response?.summary

    // Calculate statistics from API summary
    const statistics = useMemo(() => {
        if (!summary) {
            return {
                totalRevenue: 0,
                actualRevenue: 0,
                projectedRevenue: 0,
                paidAmount: 0,
                unpaidAmount: 0,
            }
        }

        return {
            totalRevenue: summary.total_allocated_amount || 0,
            actualRevenue: summary.total_actual_amount || 0,
            projectedRevenue: summary.total_projected_amount || 0,
            paidAmount: summary.total_paid_amount || 0,
            unpaidAmount: summary.total_unpaid_amount || 0,
        }
    }, [summary])

    // Handle month range change — setFilters already resets page to 1
    const handleMonthRangeChange = (range: MonthRange) => {
        setFilters({
            gte: dayjs(range.gte).format('YYYY-MM'),
            lte: dayjs(range.lte).format('YYYY-MM'),
        })
    }

    // Export handler
    const handleExport = async () => {
        try {
            setIsExporting(true)
            await exportCustomerRevenueCombined(customerId, {
                gte: filters.gte,
                lte: filters.lte,
            })
            toast.success(t('export_success'))
        } catch {
            toast.error(t('export_error'))
        } finally {
            setIsExporting(false)
        }
    }

    const columns = useMemo(() => getColumns(t), [t])

    return (
        <div className="space-y-2">
            {/* Filters */}
            <div className="flex justify-between items-center gap-2 flex-col md:flex-row">
                {/* View toggle */}
                <div className="flex gap-1 bg-gray-100 rounded-sm p-0.5">
                    <Button
                        onClick={() => setFilter('view', 'overview')}
                        variant={viewMode === 'overview' ? 'contained' : 'text'}
                        size="small"
                        className="rounded-sm"
                    >
                        <LayoutGrid className="h-4 w-4 mr-1" />
                        {t('overview.title')}
                    </Button>
                    <Button
                        onClick={() => setFilter('view', 'table')}
                        variant={viewMode === 'table' ? 'contained' : 'text'}
                        size="small"
                        className="rounded-sm"
                    >
                        <Table className="h-4 w-4 mr-1" />
                        {t('overview.detail')}
                    </Button>
                </div>

                <div className="flex items-center gap-2 flex-col md:flex-row w-full md:w-auto">
                    <MonthRangeSelector
                        className="w-full md:w-auto"
                        value={monthRange}
                        onChange={handleMonthRangeChange}
                    />
                    <Button
                        onClick={handleExport}
                        disabled={isExporting}
                        variant="outlined"
                        size="small"
                        className="rounded-sm w-full md:w-auto"
                    >
                        <Download className="h-4 w-4 mr-2" />
                        {isExporting ? t('downloading') : t('download')}
                    </Button>
                </div>
            </div>

            {/* Statistics Cards */}
            <StatisticsCards statistics={statistics} summary={summary} />

            {/* Overview or Detail Table */}
            {viewMode === 'overview' ? (
                <OverviewTable
                    filters={{ gte: filters.gte, lte: filters.lte }}
                />
            ) : (
                <DataTableCustomized
                    columns={columns}
                    rows={data}
                    loading={isLoading}
                    maxHeight="calc(100vh - 400px)"
                    pinnedHeader
                    rowKey={(row) =>
                        row.id ||
                        `${row.invoice_id || 'projected'}-${row.revenue_month}-${row.service_id}`
                    }
                    pagination={{
                        page: params.page,
                        pageSize: params.page_size,
                        total: pagination?.total || 0,
                        onPageChange: (p) => setParams({ page: p }, false),
                        onPageSizeChange: (size) =>
                            setParams({ page_size: size, page: 1 }),
                        pageSizeOptions: [20, 50, 100],
                    }}
                    emptyMessage={t('no_data')}
                />
            )}
        </div>
    )
}

export default RevenueAllocation
